import React from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useMobileMenu } from '../../App';
import Header from './Header';
import TopPromoBanner from '../ui/TopPromoBanner';
import Dashboard from '../dashboard/Dashboard';
import TryOn from '../virtual-try-on/TryOn';
import InspirationPhotoshoot from '../realistic-photoshoot/RealisticPhotoshoot';
import AIDesigner from '../ai-designer/AIDesigner';
import AIPoses from '../ai-poses/AIPoses';
import AIBackgroundChange from '../ai-background-change/AIBackgroundChange';
import AIPhotoshoot from '../ai-photoshoot/AIPhotoshoot';
import FashionExtractor from '../fashion-extractor/FashionExtractor';
import GhostMannequin from '../ghost-mannequin/GhostMannequin';
import WrinkleRemoval from '../wrinkle-removal/WrinkleRemoval';
import ImageEditing from '../image-editing/ImageEditing';
import AIWorkflow from '../ai-workflow/AIWorkflow';
import CanvasEditor from '../pages/CanvasEditor';
import Profile from '../pages/Profile';
import AccountSettings from '../pages/AccountSettings';
import UpgradePlans from '../pages/UpgradePlans';
import Contact from '../pages/Contact';
import Analytics from '../pages/Analytics';
import Gallery from '../pages/Gallery';
import CreditHistory from '../pages/CreditHistory';
import Subscription from '../pages/Subscription';
import NotFound from '../pages/NotFound';
import InternalVton from '../pages/InternalVton';
import UpperBodyFashionCatalogueWorkflow from '../ecommerce-photoshoot/UpperBodyFashionCatalogueWorkflow';
import GymWearPhotoshoot from '../wear-photoshoot/GymWearPhotoshoot';
import YogaWearPhotoshoot from '../wear-photoshoot/YogaWearPhotoshoot';

interface MainContentProps {
  isSidebarCollapsed: boolean;
}

const MainContent: React.FC<MainContentProps> = ({ isSidebarCollapsed }) => {
  const location = useLocation();
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
  const isCanvasEditor = location.pathname === '/canvas-editor';
  const isAIPoses = location.pathname === '/ai-poses';
  const isAIBackgroundChange = location.pathname === '/ai-background-change';
  const isEcommercePhotoshoot = location.pathname === '/ecommerce-photoshoot';
  const isGymWearPhotoshoot = location.pathname === '/gym-wear-photoshoot';
  const isYogaWearPhotoshoot = location.pathname === '/yoga-wear-photoshoot';
  const isCataloguePhotoshoot = isEcommercePhotoshoot || isGymWearPhotoshoot || isYogaWearPhotoshoot;
  const isAIWorkflow = location.pathname === '/ai-workflow' || isCataloguePhotoshoot;
  const isDashboard = location.pathname === '/';

  return (
    <div className={`${isCanvasEditor || isAIPoses || isAIBackgroundChange || isAIWorkflow ? 'w-full' : 'flex-1 flex flex-col'} transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
      {!isDashboard && (
        <button
          className="md:hidden fixed top-4 left-4 z-[60] bg-white/80 backdrop-blur-sm p-2 rounded-lg border border-accent-taupe shadow-sm text-text-description hover:text-text-heading transition-all"
          onClick={toggleMobileMenu}
        >
          <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
        </button>
      )}

      {!isDashboard && <TopPromoBanner />}
      {isDashboard && <Header />}
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/try-on" element={<TryOn />} />
        <Route path="/realistic-photoshoot" element={<InspirationPhotoshoot />} />
        <Route path="/ai-designer" element={<AIDesigner />} />
        <Route path="/ai-poses" element={<AIPoses />} />
        <Route path="/ai-background-change" element={<AIBackgroundChange />} />
        <Route path="/photoshoot" element={<AIPhotoshoot />} />
        <Route path="/fashion-extractor" element={<FashionExtractor />} />
        <Route path="/ghost-mannequin" element={<GhostMannequin />} />
        <Route path="/wrinkle-removal" element={<WrinkleRemoval />} />
        <Route path="/ai-wrinkle-removal" element={<WrinkleRemoval />} />
        <Route path="/image-editing" element={<ImageEditing />} />
        <Route path="/ai-workflow" element={<AIWorkflow />} />
        <Route path="/ecommerce-photoshoot" element={<UpperBodyFashionCatalogueWorkflow onBackToWorkflows={() => window.history.back()} />} />
        <Route path="/gym-wear-photoshoot" element={<GymWearPhotoshoot />} />
        <Route path="/active-wear-photoshoot" element={<Navigate to="/gym-wear-photoshoot" replace />} />
        <Route path="/yoga-wear-photoshoot" element={<YogaWearPhotoshoot />} />
        <Route path="/canvas-editor" element={<CanvasEditor />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/account-settings" element={<AccountSettings />} />
        <Route path="/upgrade" element={<UpgradePlans />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/credit-history" element={<CreditHistory />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/internal-vton" element={<InternalVton />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

export default MainContent; 